/**
 * Эпистемические классы. Клиент НЕ повышает и НЕ переименовывает класс.
 *
 * Правило spec lean-application-platform: неизвестный сервером выданный класс
 * отображается как явное «неизвестно», а не как фактический, производный или
 * отсутствующий. Поэтому здесь словарь известных кодов и честный fallback —
 * никаких `?? "official"` и никаких «похоже на производный».
 *
 * Словари хранят описания сообщений каталога; строку делает `tr` при выдаче.
 */

import { defineMessages, type Message } from "../i18n/define";
import { tr } from "../i18n/runtime";
import { lookup } from "./crops";

export interface EpistemicClass {
  /** Код ровно как его выдал сервер. */
  code: string;
  /** Подпись для человека на языке интерфейса. */
  label: string;
  /** Короткая подпись для плотных таблиц. */
  short: string;
  /** Знает ли клиент этот класс. `false` ⇒ показывается «неизвестно». */
  known: boolean;
  /** Синтетика: помечается всегда и не входит в фактические итоги. */
  synthetic: boolean;
}

const EPISTEMIC = defineMessages({
  officialLabel: { id: "common.epistemic.official.label", defaultMessage: "Официальный источник", description: "Класс данных: взято из официального источника (подпись класса целиком)" },
  officialShort: { id: "common.epistemic.official.short", defaultMessage: "источник", description: "Класс данных «официальный источник» в плотной таблице; коротко" },
  derivedLabel: { id: "common.epistemic.derived.label", defaultMessage: "Производный факт", description: "Класс данных: посчитано системой из исходных данных (подпись целиком)" },
  derivedShort: { id: "common.epistemic.derived.short", defaultMessage: "производный", description: "Класс данных «производный факт» в плотной таблице; коротко" },
  syntheticLabel: { id: "common.epistemic.synthetic.label", defaultMessage: "Синтетический (демо)", description: "Класс данных: придуманные демонстрационные данные, не факт (подпись целиком)" },
  syntheticShort: { id: "common.epistemic.synthetic.short", defaultMessage: "демо", description: "Класс данных «синтетический» в плотной таблице; коротко" },
  hypothesisLabel: { id: "common.epistemic.hypothesis.label", defaultMessage: "Гипотеза", description: "Класс данных: предположение, не подтверждённый факт (подпись целиком)" },
  hypothesisShort: { id: "common.epistemic.hypothesis.short", defaultMessage: "гипотеза", description: "Класс данных «гипотеза» в плотной таблице; коротко" },
  notGiven: { id: "common.epistemic.notGiven", defaultMessage: "Класс не указан", description: "Класс данных не пришёл с сервера" },
  unknownShort: { id: "common.epistemic.unknownShort", defaultMessage: "неизвестно", description: "Класс данных неизвестен интерфейсу; коротко, в плотной таблице" },
  unknownClass: { id: "common.epistemic.unknownClass", defaultMessage: "Неизвестный класс: {code}", description: "Сервер прислал класс данных, которого интерфейс не знает; {code} — машинный код" },
});

const KNOWN: Record<string, { label: Message; short: Message; synthetic: boolean }> = {
  official: { label: EPISTEMIC.officialLabel, short: EPISTEMIC.officialShort, synthetic: false },
  derived: { label: EPISTEMIC.derivedLabel, short: EPISTEMIC.derivedShort, synthetic: false },
  synthetic_demo: { label: EPISTEMIC.syntheticLabel, short: EPISTEMIC.syntheticShort, synthetic: true },
  hypothesis: { label: EPISTEMIC.hypothesisLabel, short: EPISTEMIC.hypothesisShort, synthetic: false },
};

export function epistemicClass(code: string | null | undefined): EpistemicClass {
  if (!code) {
    return {
      code: "",
      label: tr(EPISTEMIC.notGiven),
      short: tr(EPISTEMIC.unknownShort),
      known: false,
      synthetic: false,
    };
  }
  const known = Object.prototype.hasOwnProperty.call(KNOWN, code) ? KNOWN[code] : undefined;
  if (!known) {
    return {
      code,
      label: tr(EPISTEMIC.unknownClass, { code }),
      short: tr(EPISTEMIC.unknownShort),
      known: false,
      synthetic: false,
    };
  }
  return {
    code,
    known: true,
    label: tr(known.label),
    short: tr(known.short),
    synthetic: known.synthetic,
  };
}

/** Тяжесть замечания качества — подпись; порядок и смысл задаёт сервер. */
const SEVERITY: Record<string, Message> = defineMessages({
  blocker: { id: "common.epistemic.severity.blocker", defaultMessage: "Блокирует публикацию", description: "Тяжесть замечания по качеству данных: самое тяжёлое, блокирует публикацию среза" },
  critical: { id: "common.epistemic.severity.critical", defaultMessage: "Критично", description: "Тяжесть замечания по качеству данных" },
  high: { id: "common.epistemic.severity.high", defaultMessage: "Высокая", description: "Тяжесть замечания по качеству данных" },
  warning: { id: "common.epistemic.severity.warning", defaultMessage: "Предупреждение", description: "Тяжесть замечания по качеству данных" },
  medium: { id: "common.epistemic.severity.medium", defaultMessage: "Средняя", description: "Тяжесть замечания по качеству данных" },
  low: { id: "common.epistemic.severity.low", defaultMessage: "Низкая", description: "Тяжесть замечания по качеству данных" },
  info: { id: "common.epistemic.severity.info", defaultMessage: "Информация", description: "Тяжесть замечания по качеству данных: просто сведение" },
});

const STATUS: Record<string, Message> = defineMessages({
  open: { id: "common.epistemic.status.open", defaultMessage: "Открыто", description: "Статус замечания по качеству данных: не разобрано" },
  accepted_limitation: { id: "common.epistemic.status.accepted_limitation", defaultMessage: "Принятое ограничение", description: "Статус замечания по качеству данных: решено жить с ограничением" },
  accepted_limit: { id: "common.epistemic.status.accepted_limit", defaultMessage: "Принятое ограничение (чекпоинт)", description: "Статус замечания по качеству данных: принятое ограничение на контрольной точке приёмки" },
  deferred: { id: "common.epistemic.status.deferred", defaultMessage: "Отложено", description: "Статус замечания по качеству данных: отложено на потом" },
  resolved: { id: "common.epistemic.status.resolved", defaultMessage: "Устранено", description: "Статус замечания по качеству данных: исправлено" },
});

const SCOPE: Record<string, Message> = defineMessages({
  farmer_default: { id: "common.epistemic.scope.farmer_default", defaultMessage: "Фермерские (вид по умолчанию)", description: "Область реестра хозяйств: фермерские хозяйства, показываются по умолчанию" },
  dehqan_reserve: { id: "common.epistemic.scope.dehqan_reserve", defaultMessage: "Дехканские / резерв", description: "Область реестра хозяйств: дехканские хозяйства и резервные земли" },
  context_other: { id: "common.epistemic.scope.context_other", defaultMessage: "Прочий контекст", description: "Область реестра хозяйств: прочие субъекты, показаны для контекста" },
});

const IDENTITY: Record<string, Message> = defineMessages({
  normalized_name_cluster: { id: "common.epistemic.identity.normalized_name_cluster", defaultMessage: "Кластер нормализованного имени", description: "Как опознано хозяйство: по сходству нормализованных названий в источниках" },
  unresolved: { id: "common.epistemic.identity.unresolved", defaultMessage: "Не разрешён", description: "Как опознано хозяйство: личность не установлена" },
  aggregate: { id: "common.epistemic.identity.aggregate", defaultMessage: "Агрегат (не одно хозяйство)", description: "Как опознан субъект: это сводная строка, а не одно хозяйство" },
  source_subject: { id: "common.epistemic.identity.source_subject", defaultMessage: "Субъект источника", description: "Как опознан субъект: взят из источника как есть" },
});

const AGGREGATION: Record<string, Message> = defineMessages({
  additive_leaf: { id: "common.epistemic.aggregation.additive_leaf", defaultMessage: "Аддитивный лист", description: "Роль величины в сводках: исходное значение, которое можно суммировать" },
  derived_total: { id: "common.epistemic.aggregation.derived_total", defaultMessage: "Производный итог", description: "Роль величины в сводках: итог, посчитанный из других" },
  subset: { id: "common.epistemic.aggregation.subset", defaultMessage: "Подмножество", description: "Роль величины в сводках: часть другой величины, не суммируется с ней" },
  control: { id: "common.epistemic.aggregation.control", defaultMessage: "Контрольная величина", description: "Роль величины в сводках: используется для сверки, не для суммы" },
});

/**
 * Вид субъекта в хранилище.
 *
 * Словарь ОТКРЫТ: коды приходят из канонической базы, и незнакомый называется
 * незнакомым, а не переводится на глазок. До этого словаря карточка печатала
 * код хранилища как есть — «agricultural_subject» под подписью «Тип субъекта»,
 * рядом с двумя соседними строками того же списка, у которых подписи русские.
 */
const ACTOR_TYPE: Record<string, Message> = defineMessages({
  agricultural_subject: { id: "common.epistemic.actorType.agricultural_subject", defaultMessage: "Сельхозсубъект", description: "Вид субъекта в карточке хозяйства: сельхозсубъект" },
  legal_entity: { id: "common.epistemic.actorType.legal_entity", defaultMessage: "Юридическое лицо", description: "Вид субъекта в карточке хозяйства" },
  dehqan_household: { id: "common.epistemic.actorType.dehqan_household", defaultMessage: "Дехканское хозяйство", description: "Вид субъекта в карточке хозяйства" },
  dehqan_aggregate: { id: "common.epistemic.actorType.dehqan_aggregate", defaultMessage: "Дехканские хозяйства (агрегат)", description: "Вид субъекта: сводная строка по многим дехканским хозяйствам" },
  reserve_land: { id: "common.epistemic.actorType.reserve_land", defaultMessage: "Резервные земли", description: "Вид субъекта: резервные земли без хозяина" },
  institution: { id: "common.epistemic.actorType.institution", defaultMessage: "Учреждение", description: "Вид субъекта в карточке хозяйства: учреждение" },
});

export const severityLabel = (code: string | null | undefined) =>
  lookup(SEVERITY, code);
export const statusLabel = (code: string | null | undefined) => lookup(STATUS, code);
export const scopeLabel = (code: string | null | undefined) => lookup(SCOPE, code);
export const identityLabel = (code: string | null | undefined) =>
  lookup(IDENTITY, code);
export const aggregationLabel = (code: string | null | undefined) =>
  lookup(AGGREGATION, code);
export const actorTypeLabel = (code: string | null | undefined) =>
  lookup(ACTOR_TYPE, code);

/**
 * Предмет замечания качества.
 *
 * Список видов ОТКРЫТ: часть кодов приходит прямо из файла приёмки. Поэтому
 * незнакомый код называется незнакомым, а не переводится на глазок — молчаливый
 * «Контур» на чужом коде однажды назвал бы контуром не контур.
 */
const SUBJECT_TYPE: Record<string, Message> = defineMessages({
  contour: { id: "common.epistemic.subject.contour", defaultMessage: "Контур", description: "Предмет замечания по качеству данных: контур поля" },
  contour_area: { id: "common.epistemic.subject.contour_area", defaultMessage: "Площадь контура", description: "Предмет замечания по качеству данных: площадь контура" },
  source_record: { id: "common.epistemic.subject.source_record", defaultMessage: "Строка источника", description: "Предмет замечания по качеству данных: строка исходного файла" },
  f1_block: { id: "common.epistemic.subject.f1_block", defaultMessage: "Блок формы Ф-1", description: "Предмет замечания по качеству данных: блок отчётной формы Ф-1" },
});

/**
 * Вид принятого среза.
 *
 * `canonical_registry` — имя вида в базе, а не подпись для человека: дословно
 * в шапке экрана оно читается как техническая утечка.
 */
const SOURCE_KIND: Record<string, Message> = defineMessages({
  canonical_registry: { id: "common.epistemic.sourceKind.canonical_registry", defaultMessage: "канонический реестр", description: "Вид принятого среза данных в шапке экрана (строчная)" },
});

const NOT_GIVEN = defineMessages({
  subject: { id: "common.epistemic.subject.empty", defaultMessage: "Предмет не указан", description: "Предмет замечания по качеству данных не пришёл" },
  sourceKind: { id: "common.epistemic.sourceKind.empty", defaultMessage: "вид не указан", description: "Вид принятого среза данных не пришёл (строчная)" },
});

export const subjectTypeLabel = (code: string | null | undefined) =>
  code ? lookup(SUBJECT_TYPE, code) : { label: tr(NOT_GIVEN.subject), known: false };
export const sourceKindLabel = (code: string | null | undefined) =>
  code ? lookup(SOURCE_KIND, code) : { label: tr(NOT_GIVEN.sourceKind), known: false };

/** Порядок показа тяжести: сначала то, что блокирует. */
export const SEVERITY_ORDER = [
  "blocker",
  "critical",
  "high",
  "warning",
  "medium",
  "low",
  "info",
];

export function severityRank(code: string): number {
  const index = SEVERITY_ORDER.indexOf(code);
  return index === -1 ? SEVERITY_ORDER.length : index;
}
